/* eslint-disable react-refresh/only-export-components */
import { useCallback, useEffect, useRef, useState } from 'react';

import { mediaApi, type MediaLibraryItem } from '@/lib/catalog/catalog-api';
import type { MediaLink, MediaLinkInput, MediaRole } from '@/lib/catalog/types';

import { AdminIcon } from './icon';

export interface UploaderItem {
  mediaId: string;
  url: string;
  altText: string | null;
  role: MediaRole;
  filename: string;
  mimeType: string;
}

interface MediaUploaderProps {
  value?: UploaderItem[];
  onChange?: (items: UploaderItem[]) => void;
  /** Si true, autorise plusieurs fichiers — sinon un seul (ex. hero). */
  multiple?: boolean;
  /** Rôle par défaut pour les médias uploadés ('gallery' par défaut, 'hero' pour les bannières). */
  defaultRole?: MediaRole;
  /** Texte du bouton principal. */
  label?: string;
}

export function mediaLinksToInputs(items: UploaderItem[]): MediaLinkInput[] {
  return items.map((it, i) => ({ mediaId: it.mediaId, role: it.role, sortOrder: i }));
}

export function linksToItems(links: MediaLink[] | undefined): UploaderItem[] {
  return (links ?? [])
    .filter((l) => !!l.url)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((l) => ({
      mediaId: l.mediaId,
      url: l.url!,
      altText: l.altText,
      role: l.role,
      filename: '',
      mimeType: l.mimeType ?? 'image/*',
    }));
}

export function MediaUploader({
  value = [],
  onChange,
  multiple = true,
  defaultRole = 'gallery',
  label = 'Ajouter des images',
}: MediaUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<{ name: string; pct: number }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const set = useCallback(
    (next: UploaderItem[]) => onChange?.(next),
    [onChange],
  );

  // 4.2 — persiste le texte alternatif sur le média (au blur du champ).
  const persistAlt = useCallback(async (mediaId: string, alt: string) => {
    try {
      await mediaApi.update(mediaId, { altText: alt });
    } catch {
      /* silencieux : la valeur locale reste, l'utilisateur peut réessayer */
    }
  }, []);

  // 4.3 — rattache des médias existants choisis dans la bibliothèque.
  const addFromLibrary = useCallback(
    (assets: MediaLibraryItem[]) => {
      const existingIds = new Set(value.map((v) => v.mediaId));
      const fresh = assets
        .filter((a) => !existingIds.has(a.id))
        .map((a) => ({
          mediaId: a.id,
          url: a.url,
          altText: a.altText,
          role: defaultRole,
          filename: a.filename,
          mimeType: a.mimeType,
        }));
      if (!fresh.length) return;
      if (multiple) set([...value, ...fresh]);
      else set(fresh.slice(0, 1));
    },
    [defaultRole, multiple, set, value],
  );

  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files?.length) return;
      setError(null);
      const list = Array.from(files);
      const queue = multiple ? list : list.slice(0, 1);
      const initial = queue.map((f) => ({ name: f.name, pct: 0 }));
      setProgress(initial);
      const uploaded: UploaderItem[] = [];
      for (let i = 0; i < queue.length; i++) {
        const f = queue[i];
        try {
          // Pas d'event de progress sur axios sans config — on simule à 50% puis 100%.
          setProgress((p) =>
            p.map((row, idx) => (idx === i ? { ...row, pct: 30 } : row)),
          );
          const asset = await mediaApi.upload(f);
          setProgress((p) =>
            p.map((row, idx) => (idx === i ? { ...row, pct: 100 } : row)),
          );
          uploaded.push({
            mediaId: asset.id,
            url: asset.url,
            altText: asset.altText,
            role: defaultRole,
            filename: asset.filename,
            mimeType: asset.mimeType,
          });
        } catch (e) {
          setError((e as Error).message ?? 'Erreur upload');
        }
      }
      setProgress([]);
      if (multiple) set([...value, ...uploaded]);
      else set(uploaded.slice(0, 1));
    },
    [defaultRole, multiple, set, value],
  );

  const remove = (idx: number) => set(value.filter((_, i) => i !== idx));
  const move = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= value.length) return;
    const next = [...value];
    [next[idx], next[target]] = [next[target], next[idx]];
    set(next);
  };
  const updateAlt = (idx: number, alt: string) =>
    set(value.map((it, i) => (i === idx ? { ...it, altText: alt } : it)));

  return (
    <div>
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          void handleFiles(e.dataTransfer.files);
        }}
        style={{
          border: '1px dashed var(--a-border-strong)',
          borderRadius: 'var(--a-r-sm)',
          padding: 16,
          textAlign: 'center',
          background: 'var(--a-surface-2)',
          cursor: 'pointer',
        }}
        onClick={() => inputRef.current?.click()}
      >
        <AdminIcon n="upDown" s={20} />
        <div style={{ marginTop: 6, fontSize: 13, fontWeight: 500 }}>{label}</div>
        <div className="muted" style={{ fontSize: 11 }}>
          Glisser-déposer ou cliquer · JPG / PNG / WebP / MP4 — max 10 Mo (image) / 50 Mo (vidéo)
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*,video/mp4,application/pdf"
          multiple={multiple}
          style={{ display: 'none' }}
          onChange={(e) => void handleFiles(e.target.files)}
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => setPickerOpen(true)}
        >
          <AdminIcon n="search" s={13} /> Choisir dans la bibliothèque
        </button>
      </div>

      {progress.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 12 }}>
          {progress.map((p, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
              <span className="tn">{p.pct}%</span>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div style={{ marginTop: 8, color: 'var(--a-danger)', fontSize: 12 }}>
          {error}
        </div>
      )}

      {value.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
            gap: 8,
            marginTop: 12,
          }}
        >
          {value.map((item, idx) => (
            <div
              key={item.mediaId}
              style={{
                border: '1px solid var(--a-border)',
                borderRadius: 'var(--a-r-sm)',
                overflow: 'hidden',
                background: '#fff',
                position: 'relative',
              }}
            >
              <div
                style={{
                  aspectRatio: '1 / 1',
                  background: 'var(--a-surface-2)',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                {item.mimeType.startsWith('image/') ? (
                  <img
                    src={item.url}
                    alt={item.altText ?? ''}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <AdminIcon n="box" s={28} />
                )}
              </div>
              <div style={{ padding: 6 }}>
                <input
                  value={item.altText ?? ''}
                  onChange={(e) => updateAlt(idx, e.target.value)}
                  onBlur={(e) => void persistAlt(item.mediaId, e.target.value)}
                  placeholder="Texte alternatif"
                  title="Texte alternatif (accessibilité, SEO, recherche) — enregistré à la sortie du champ"
                  className="aform-i"
                  style={{ fontSize: 11, height: 24 }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                  <button
                    type="button"
                    className="top-btn"
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                    style={{ opacity: idx === 0 ? 0.3 : 1 }}
                  >
                    <AdminIcon n="chevL" s={12} />
                  </button>
                  <button
                    type="button"
                    className="top-btn"
                    onClick={() => move(idx, 1)}
                    disabled={idx === value.length - 1}
                    style={{ opacity: idx === value.length - 1 ? 0.3 : 1 }}
                  >
                    <AdminIcon n="chevR" s={12} />
                  </button>
                  <button
                    type="button"
                    className="top-btn"
                    onClick={() => remove(idx)}
                    style={{ color: 'var(--a-danger)' }}
                  >
                    <AdminIcon n="close" s={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {pickerOpen && (
        <MediaLibraryPicker
          multiple={multiple}
          onClose={() => setPickerOpen(false)}
          onConfirm={addFromLibrary}
        />
      )}
    </div>
  );
}

/** 4.3 — sélecteur « choisir dans la bibliothèque » : réutiliser un média existant. */
function MediaLibraryPicker({
  multiple,
  onClose,
  onConfirm,
}: {
  multiple: boolean;
  onClose: () => void;
  onConfirm: (assets: MediaLibraryItem[]) => void;
}) {
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<MediaLibraryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Record<string, MediaLibraryItem>>({});

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const t = setTimeout(() => {
      mediaApi
        .list({ search: search.trim() || undefined, kind: 'image', pageSize: 48 })
        .then((r) => alive && setItems(r.items))
        .catch(() => alive && setItems([]))
        .finally(() => alive && setLoading(false));
    }, 250);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [search]);

  const toggle = (a: MediaLibraryItem) =>
    setSelected((prev) => {
      if (prev[a.id]) {
        const next = { ...prev };
        delete next[a.id];
        return next;
      }
      return multiple ? { ...prev, [a.id]: a } : { [a.id]: a };
    });

  const chosen = Object.values(selected);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--a-surface)',
          borderRadius: 'var(--a-r)',
          width: 'min(760px, 92vw)',
          maxHeight: '86vh',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid var(--a-border)',
        }}
      >
        <div
          style={{
            padding: '12px 16px',
            borderBottom: '1px solid var(--a-border)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <strong style={{ flex: 1 }}>Choisir dans la bibliothèque</strong>
          <button type="button" className="top-btn" onClick={onClose}>
            <AdminIcon n="close" s={14} />
          </button>
        </div>
        <div style={{ padding: '10px 16px' }}>
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par nom ou texte alternatif…"
            className="aform-i"
            style={{ width: '100%' }}
          />
        </div>
        <div style={{ padding: '0 16px 12px', overflow: 'auto', flex: 1 }}>
          {loading ? (
            <div className="muted" style={{ padding: 20, textAlign: 'center' }}>
              Chargement…
            </div>
          ) : items.length === 0 ? (
            <div className="muted" style={{ padding: 20, textAlign: 'center' }}>
              Aucun média — téléversez-en depuis la zone ci-dessus ou la Bibliothèque d'images.
            </div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))',
                gap: 8,
              }}
            >
              {items.map((a) => {
                const on = !!selected[a.id];
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => toggle(a)}
                    title={a.altText ?? a.filename}
                    style={{
                      position: 'relative',
                      padding: 0,
                      border: on ? '2px solid var(--a-primary)' : '1px solid var(--a-border)',
                      borderRadius: 'var(--a-r-sm)',
                      overflow: 'hidden',
                      background: '#fff',
                      aspectRatio: '1 / 1',
                      cursor: 'pointer',
                    }}
                  >
                    <img
                      src={a.url}
                      alt={a.altText ?? ''}
                      loading="lazy"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    {on && (
                      <span
                        style={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          background: 'var(--a-primary)',
                          color: '#fff',
                          borderRadius: '50%',
                          width: 18,
                          height: 18,
                          display: 'grid',
                          placeItems: 'center',
                        }}
                      >
                        <AdminIcon n="check" s={11} />
                      </span>
                    )}
                    {a.usageCount > 0 && (
                      <span
                        style={{
                          position: 'absolute',
                          bottom: 2,
                          left: 2,
                          fontSize: 9,
                          background: 'rgba(0,0,0,0.6)',
                          color: '#fff',
                          borderRadius: 4,
                          padding: '0 4px',
                        }}
                      >
                        ×{a.usageCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div
          style={{
            padding: '12px 16px',
            borderTop: '1px solid var(--a-border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
          }}
        >
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
            Annuler
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!chosen.length}
            onClick={() => {
              onConfirm(chosen);
              onClose();
            }}
          >
            Ajouter{chosen.length ? ` (${chosen.length})` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}
